import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

// ============================================================
// TYPES — три источника, один discriminated union
// ============================================================
//
// Сервер /api/personal-calendar возвращает массив UnifiedCalendarEvent, который
// клиент трансформирует в FullCalendar-совместимые объекты (FCPersonalEvent) с
// kind-discriminator в extendedProps. UI читает kind, чтобы выбрать поведение
// (editable, click target, color source).

export type EventKind = 'personal' | 'meeting' | 'booking'

export interface FCPersonalEvent {
  id: string
  title: string
  start: string
  end: string
  backgroundColor: string
  borderColor: string
  textColor: string
  editable: boolean
  extendedProps: {
    eventId: string
    kind: EventKind
    color: string
    // personal-specific
    ownerId?: string
    notes?: string | null
    // meeting-specific
    dealId?: string
    dealName?: string
    stageColor?: string | null
    meetingType?: 'STUDIO' | 'ONLINE'
    // booking-specific
    projectId?: string
    projectName?: string
    projectColor?: string
    // common
    participants?: Array<{ id: string; name: string | null }>
    [key: string]: unknown
  }
}

// Серверные shapes (соответствуют UnifiedCalendarEvent из personal-calendar.service.ts).
// Поля Date → строки на проводе (NextResponse сериализует).
interface ServerPersonalEvent {
  kind: 'personal'
  editable: true
  id: string
  ownerId: string
  title: string
  notes: string | null
  startDatetime: string
  endDatetime: string
  color: string
}

interface ServerMeetingEvent {
  kind: 'meeting'
  editable: false
  id: string
  dealId: string
  startDatetime: string
  endDatetime: string
  note: string | null
  type: 'STUDIO' | 'ONLINE'
  deal: { id: string; name: string; stage: { color: string | null } | null }
  participants: Array<{ id: string; name: string | null }>
}

interface ServerBookingEvent {
  kind: 'booking'
  editable: false
  id: string
  projectId: string
  startDatetime: string
  endDatetime: string
  note: string | null
  project: { id: string; name: string; color: string }
  participants: Array<{ id: string; name: string | null }>
}

type ServerUnifiedEvent = ServerPersonalEvent | ServerMeetingEvent | ServerBookingEvent

export interface PersonalEventData {
  id: string
  ownerId: string
  title: string
  notes: string | null
  startDatetime: string
  endDatetime: string
  color: string
}

interface PersonalCalendarState {
  events: FCPersonalEvent[]
  loading: boolean
  sidebarOpen: boolean
  selectedEvent: PersonalEventData | null
  dateRange: { from: string; to: string } | null
  error: string | null
}

const initialState: PersonalCalendarState = {
  events: [],
  loading: false,
  sidebarOpen: false,
  selectedEvent: null,
  dateRange: null,
  error: null
}

// ============================================================
// COLOR HELPERS — локальная копия per PATTERNS §9 YAGNI
// ============================================================
//
// Дублирует lightenForBackground из meeting-calendar / booking-calendar — пока
// три independent кёрндлеры. Когда появится четвёртый клиент — выделим в shared.

const DEFAULT_PERSONAL_COLOR = '#6366F1'
const DEFAULT_MEETING_COLOR = '#6B7280' // Phase 4 P04-04 — neutral gray fallback
const DEFAULT_BOOKING_COLOR = '#6366F1'

export function lightenForBackground(color: string, tint = 0.2): string {
  const hex = color.trim()
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex)

  if (!match) return color

  const int = parseInt(match[1], 16)
  const r = (int >> 16) & 0xff
  const g = (int >> 8) & 0xff
  const b = int & 0xff
  const mix = (channel: number) => Math.round(channel * tint + 255 * (1 - tint))
  const toHex = (n: number) => n.toString(16).padStart(2, '0')

  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`
}

// ============================================================
// MAPPERS — server event → FullCalendar event
// ============================================================

function mapPersonalEventToFC(p: ServerPersonalEvent): FCPersonalEvent {
  const color = p.color || DEFAULT_PERSONAL_COLOR
  const background = lightenForBackground(color)

  return {
    id: p.id,
    title: p.title,
    start: p.startDatetime,
    end: p.endDatetime,
    backgroundColor: background,
    borderColor: color,
    textColor: '#1f2937',
    editable: true,
    extendedProps: {
      eventId: p.id,
      kind: 'personal',
      color,
      ownerId: p.ownerId,
      notes: p.notes
    }
  }
}

function mapMeetingEventToFC(m: ServerMeetingEvent): FCPersonalEvent {
  const stageColor = m.deal.stage?.color ?? null
  const color = stageColor ?? DEFAULT_MEETING_COLOR
  const background = lightenForBackground(color)

  return {
    id: m.id,
    title: m.deal.name,
    start: m.startDatetime,
    end: m.endDatetime,
    backgroundColor: background,
    borderColor: color,
    textColor: '#1f2937',
    editable: false,
    extendedProps: {
      eventId: m.id,
      kind: 'meeting',
      color,
      dealId: m.dealId,
      dealName: m.deal.name,
      stageColor,
      meetingType: m.type,
      participants: m.participants,
      notes: m.note
    }
  }
}

function mapBookingEventToFC(b: ServerBookingEvent): FCPersonalEvent {
  const color = b.project.color || DEFAULT_BOOKING_COLOR
  const background = lightenForBackground(color)

  return {
    id: b.id,
    title: b.project.name,
    start: b.startDatetime,
    end: b.endDatetime,
    backgroundColor: background,
    borderColor: color,
    textColor: '#1f2937',
    editable: false,
    extendedProps: {
      eventId: b.id,
      kind: 'booking',
      color,
      projectId: b.projectId,
      projectName: b.project.name,
      projectColor: color,
      participants: b.participants,
      notes: b.note
    }
  }
}

function mapUnifiedToFC(event: ServerUnifiedEvent): FCPersonalEvent {
  switch (event.kind) {
    case 'personal':
      return mapPersonalEventToFC(event)
    case 'meeting':
      return mapMeetingEventToFC(event)
    case 'booking':
      return mapBookingEventToFC(event)
  }
}

// ============================================================
// API ERROR (client-side) — mirror meeting-calendar.ts
// ============================================================

export class ApiClientError extends Error {
  public readonly code?: string
  public readonly status?: number

  constructor(message: string, opts?: { code?: string; status?: number }) {
    super(message)
    this.name = 'ApiClientError'
    this.code = opts?.code
    this.status = opts?.status
  }
}

// ============================================================
// THUNKS
// ============================================================

export const fetchPersonalCalendarEvents = createAsyncThunk(
  'personalCalendar/fetchEvents',
  async ({ from, to, ownerId }: { from: string; to: string; ownerId?: string }) => {
    const params = new URLSearchParams({ from, to })

    if (ownerId) params.set('ownerId', ownerId)
    const res = await fetch(`/api/personal-calendar?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new ApiClientError(json.error ?? 'Не удалось загрузить события', { status: res.status })
    }

    const mapped = (json.data as ServerUnifiedEvent[]).map(mapUnifiedToFC)

    // Помечаем события, которые пересекаются с другим событием той же ленты
    // по времени — на personal-calendar ВСЕ события принадлежат текущему юзеру
    // (или он в их участниках), поэтому пересечение по времени = занятость
    // его самого. Красный треугольник в карточке (см. *EventContent).
    return markOverlaps(mapped)
  }
)

function markOverlaps(events: FCPersonalEvent[]): FCPersonalEvent[] {
  const times = events.map(e => ({ start: new Date(e.start).getTime(), end: new Date(e.end).getTime() }))

  return events.map((e, i) => {
    const overlaps = events.some((other, j) => {
      if (i === j) return false

      return times[j].start < times[i].end && times[j].end > times[i].start
    })

    if (!overlaps) return e

    return {
      ...e,
      extendedProps: { ...e.extendedProps, hasParticipantOverlap: true }
    }
  })
}

export const createPersonalEvent = createAsyncThunk(
  'personalCalendar/createEvent',
  async (payload: {
    title: string
    startDateTime: string
    endDateTime: string
    notes?: string
    color?: string
  }) => {
    const res = await fetch('/api/personal-calendar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    const json = await res.json()

    if (!json.success) {
      throw new ApiClientError(json.error ?? 'Ошибка создания события', {
        code: json.code,
        status: res.status
      })
    }

    const created = json.data as PersonalEventData

    return mapPersonalEventToFC({
      kind: 'personal',
      editable: true,
      id: created.id,
      ownerId: created.ownerId,
      title: created.title,
      notes: created.notes,
      startDatetime: created.startDatetime,
      endDatetime: created.endDatetime,
      color: created.color
    })
  }
)

export const updatePersonalEvent = createAsyncThunk(
  'personalCalendar/updateEvent',
  async ({
    id,
    payload
  }: {
    id: string
    payload: Partial<{
      title: string
      startDateTime: string
      endDateTime: string
      notes: string | null
      color: string
    }>
  }) => {
    const res = await fetch(`/api/personal-calendar/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    const json = await res.json()

    if (!json.success) {
      throw new ApiClientError(json.error ?? 'Ошибка обновления события', {
        code: json.code,
        status: res.status
      })
    }

    const updated = json.data as PersonalEventData

    return mapPersonalEventToFC({
      kind: 'personal',
      editable: true,
      id: updated.id,
      ownerId: updated.ownerId,
      title: updated.title,
      notes: updated.notes,
      startDatetime: updated.startDatetime,
      endDatetime: updated.endDatetime,
      color: updated.color
    })
  }
)

export const deletePersonalEvent = createAsyncThunk(
  'personalCalendar/deleteEvent',
  async (id: string) => {
    const res = await fetch(`/api/personal-calendar/${id}`, { method: 'DELETE' })
    const json = await res.json()

    if (!json.success) {
      throw new ApiClientError(json.error ?? 'Ошибка удаления события', {
        code: json.code,
        status: res.status
      })
    }

    return id
  }
)

// ============================================================
// SLICE
// ============================================================

export const personalCalendarSlice = createSlice({
  name: 'personalCalendar',
  initialState,
  reducers: {
    openSidebar: state => {
      return { ...state, sidebarOpen: true }
    },
    closeSidebar: state => {
      return { ...state, sidebarOpen: false, selectedEvent: null }
    },
    setSelectedEvent: (state, action: PayloadAction<PersonalEventData | null>) => {
      return { ...state, selectedEvent: action.payload }
    },
    setDateRange: (state, action: PayloadAction<{ from: string; to: string } | null>) => {
      return { ...state, dateRange: action.payload }
    }
  },
  extraReducers: builder => {
    // fetch
    builder.addCase(fetchPersonalCalendarEvents.pending, state => {
      return { ...state, loading: true, error: null }
    })

    builder.addCase(fetchPersonalCalendarEvents.fulfilled, (state, action) => {
      return { ...state, events: action.payload, loading: false }
    })

    builder.addCase(fetchPersonalCalendarEvents.rejected, (state, action) => {
      return {
        ...state,
        loading: false,
        error: action.error.message ?? 'Не удалось загрузить события'
      }
    })

    // create
    builder.addCase(createPersonalEvent.fulfilled, (state, action) => {
      return {
        ...state,
        events: [...state.events, action.payload],
        sidebarOpen: false,
        selectedEvent: null
      }
    })

    builder.addCase(createPersonalEvent.rejected, (state, action) => {
      return { ...state, error: action.error.message ?? 'Ошибка создания события' }
    })

    // update
    builder.addCase(updatePersonalEvent.fulfilled, (state, action) => {
      const updatedEvent = action.payload

      return {
        ...state,
        events: state.events.map(evt => (evt.id === updatedEvent.id ? updatedEvent : evt)),
        sidebarOpen: false,
        selectedEvent: null
      }
    })

    builder.addCase(updatePersonalEvent.rejected, (state, action) => {
      return { ...state, error: action.error.message ?? 'Ошибка обновления события' }
    })

    // delete
    builder.addCase(deletePersonalEvent.fulfilled, (state, action) => {
      const deletedId = action.payload

      return {
        ...state,
        events: state.events.filter(evt => evt.id !== deletedId),
        sidebarOpen: false,
        selectedEvent: null
      }
    })

    builder.addCase(deletePersonalEvent.rejected, (state, action) => {
      return { ...state, error: action.error.message ?? 'Ошибка удаления события' }
    })
  }
})

export const { openSidebar, closeSidebar, setSelectedEvent, setDateRange } =
  personalCalendarSlice.actions

export default personalCalendarSlice.reducer
